import { useState } from 'react';
import { db, storage } from '../lib/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { X, Upload, FileText, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import * as pdfjsLib from 'pdfjs-dist';
// @ts-ignore - Vite specific URL import
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';
import { extractAndFormatPDF } from '../lib/pdfUtils';
import { api, ApiNotConfiguredError } from '../lib/apiClient';
import { parseCanonicalStatuteContent, parseLexgatStatuteJsonPayload } from '../lib/statuteFormat';
import { ModalShell } from './ui/ModalShell';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface UploadStatuteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function UploadStatuteModal({ isOpen, onClose }: UploadStatuteModalProps) {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Civil');
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [skipSemanticIndexing, setSkipSemanticIndexing] = useState(false);

  const handleUpload = async () => {
    if (!title.trim() || !file) {
      showError("Title and a file are required.");
      return;
    }

    if (!user) {
      showError("You must be logged in.");
      return;
    }

    setIsUploading(true);
    setProgress(0);

    try {
      let isJsonUpload = file.name.toLowerCase().endsWith('.json');
      let extractedText = '';
      let jsonTooLargeForFirestore = false;

      // 1. Extract Text or Parse JSON
      if (isJsonUpload) {
        setProgress(20);
        const text = await file.text();
        let parsedJson: unknown;
        try {
          parsedJson = JSON.parse(text) as unknown;
        } catch {
          throw new Error('Invalid JSON format. Please upload a valid JSON statute file.');
        }
        const parsedV2Like = parseLexgatStatuteJsonPayload(parsedJson);
        const parsed = parseCanonicalStatuteContent(text);
        if (!parsed && !parsedV2Like) {
          throw new Error('Invalid JSON format. Use an array of sections or { "schemaVersion":"v1", "sections":[...] }.');
        }
        // Firestore doc max is 1 MiB. Large JSON should be read from Storage URL
        // in the Library instead of inlining full payload in the doc.
        const textBytes = new TextEncoder().encode(text).length;
        const MAX_INLINE_BYTES = 900_000;
        if (textBytes > MAX_INLINE_BYTES) {
          extractedText = '';
          jsonTooLargeForFirestore = true;
        } else {
          extractedText = text;
          jsonTooLargeForFirestore = false;
        }
      } else {
        setProgress(5);
        extractedText = await extractAndFormatPDF(file, (p) => setProgress(p));
      }
      
      // 2. Upload file to Storage (optional for JSON, but good for backup)
      setProgress(40);
      const storageRef = ref(storage, `statutes/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const fileUrl = await getDownloadURL(storageRef);
      setProgress(60);

      // 3. Save to Firestore
      const statuteData = {
        title: title.trim(),
        description: description.trim(),
        category,
        content: extractedText,
        contentStoredInFile: isJsonUpload && jsonTooLargeForFirestore,
        storagePath: storageRef.fullPath,
        fileUrl,
        uploadedBy: user.uid,
        createdAt: new Date().toISOString()
      };

      const statuteRef = await addDoc(collection(db, 'statutes'), statuteData);

      // 4. Canonical JSON is already stored in `content`, no subcollection write needed.
      setProgress(80);

      // 5. Trigger backend ingestion (chunk + embed + index into the vector store)
      // unless explicitly skipped by the admin.
      setProgress(90);
      if (!skipSemanticIndexing) {
        try {
          if (api.isConfigured()) {
            await api.post(`/api/ingest/statute/${statuteRef.id}`);
          } else {
            console.warn('Skipping vector ingestion: VITE_API_BASE_URL is not configured.');
          }
        } catch (ingestErr) {
          if (ingestErr instanceof ApiNotConfiguredError) {
            console.warn(ingestErr.message);
          } else {
            console.error('Vector ingestion failed (statute is still in Firestore):', ingestErr);
            showError('Uploaded, but semantic indexing failed. Run the backfill CLI to retry.');
          }
        }
      }

      setProgress(100);
      showSuccess(
        skipSemanticIndexing
          ? "Statute uploaded successfully. Semantic indexing was skipped."
          : jsonTooLargeForFirestore
            ? "Statute uploaded successfully. Large JSON will be loaded from Storage."
            : "Statute uploaded and processed successfully.",
      );
      setTimeout(() => {
        onClose();
        setTitle('');
        setDescription('');
        setFile(null);
        setProgress(0);
        setSkipSemanticIndexing(false);
      }, 500);

    } catch (err: any) {
      console.error("Failed to process statute:", err);
      showError(err.message || "An error occurred during upload.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <ModalShell
      open={isOpen}
      onClose={onClose}
      size="lg"
      zIndex={50}
      panelClassName="rounded-3xl shadow-xl"
      closeOnBackdrop={!isUploading}
    >
        <div className="p-6 border-b border-border-light flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 text-primary rounded-xl flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <h2 className="text-xl font-bold tracking-tight text-text-main">Upload Statute</h2>
          </div>
          <button 
            onClick={onClose}
            disabled={isUploading}
            className="p-2 hover:bg-bg-secondary rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-[#64748B]" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-5">
          <div>
            <label className="block text-sm font-semibold text-text-main mb-2">Statute Title *</label>
            <input 
              type="text" 
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g., Code of Civil Procedure, 1908"
              className="w-full bg-bg-secondary border border-border-light rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none"
              disabled={isUploading}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-text-main mb-2">Category *</label>
            <div className="relative">
               <select 
                 value={category}
                 onChange={e => setCategory(e.target.value)}
                 className="w-full bg-bg-secondary border border-border-light rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none appearance-none"
                 disabled={isUploading}
               >
                 <option value="Civil">Civil</option>
                 <option value="Criminal">Criminal</option>
                 <option value="Family">Family</option>
                 <option value="Writ">Constitutional / Writ</option>
                 <option value="Rent">Rent / Tenancy</option>
                 <option value="Revenue">Revenue / Land</option>
                 <option value="Banking">Banking / Financial Recovery</option>
                 <option value="Consumer">Consumer Protection</option>
                 <option value="NAB">NAB / Accountability</option>
                 <option value="FIA">FIA / Cybercrime / Federal Offences</option>
                 <option value="General">General Law (all proceedings)</option>
                 <option value="Other">Other</option>
               </select>
               <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                  <svg className="w-4 h-4 text-[#64748B]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
               </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-text-main mb-2">Short Description</label>
            <textarea 
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of the statute..."
              className="w-full bg-bg-secondary border border-border-light rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none resize-none h-24"
              disabled={isUploading}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-text-main mb-2">PDF Document *</label>
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border-light rounded-2xl bg-bg-secondary hover:bg-[#F1F5F9] transition-colors cursor-pointer group relative">
              <div className="flex flex-col items-center justify-center pt-5 pb-6 text-center">
                <Upload className="w-8 h-8 text-[#94A3B8] mb-3 group-hover:text-primary transition-colors" />
                <p className="mb-1 text-sm font-medium text-text-main">
                  {file ? file.name : <><span className="text-primary">Click to upload</span> or drag and drop</>}
                </p>
                <p className="text-xs text-[#64748B]">PDF or JSON (canonical format)</p>
              </div>
              <input 
                type="file" 
                accept="application/pdf,application/json"
                className="hidden" 
                onChange={e => setFile(e.target.files?.[0] || null)}
                disabled={isUploading}
              />
            </label>
          </div>

          <label className="flex items-start gap-3 p-3 rounded-xl border border-border-light bg-bg-secondary">
            <input
              type="checkbox"
              checked={skipSemanticIndexing}
              onChange={(e) => setSkipSemanticIndexing(e.target.checked)}
              disabled={isUploading}
              className="mt-0.5 h-4 w-4 rounded border-border-light text-primary focus:ring-primary"
            />
            <span className="text-sm text-[#475569] leading-6">
              Skip semantic indexing for this upload (do not run backend/Vertex ingestion now).
            </span>
          </label>
          
          {isUploading && (
            <div className="pt-2">
              <div className="flex items-center justify-between text-xs font-medium text-[#64748B] mb-2">
                <span>Processing PDF & Uploading...</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-bg-secondary rounded-full h-2 overflow-hidden">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border-light bg-bg-secondary shrink-0 flex justify-end gap-3">
          <button 
            type="button" 
            onClick={onClose}
            disabled={isUploading}
            className="px-5 py-2.5 text-sm font-semibold text-[#64748B] hover:text-text-main transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button 
            type="button" 
            onClick={handleUpload}
            disabled={isUploading || !title.trim() || !file}
            className="btn-primary flex items-center gap-2 px-6 py-2.5 text-sm disabled:opacity-50"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload & Process
              </>
            )}
          </button>
        </div>
    </ModalShell>
  );
}
